import { motion, useInView } from 'framer-motion';
import { useRef } from 'react';

function SponsorBlock({ sponsor, index, isInView }) {
  const rawUrl = (sponsor.website_url || '').trim();
  const websiteUrl = rawUrl && !rawUrl.startsWith('http') ? `https://${rawUrl}` : rawUrl;
  const hasLink = !!websiteUrl;
  const Tag = hasLink ? 'a' : 'div';
  const linkProps = hasLink ? { href: websiteUrl, target: '_blank', rel: 'noopener noreferrer' } : {};

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={isInView ? { opacity: 1, y: 0 } : {}}
      transition={{ duration: 0.5, delay: index * 0.07 }}
    >
      <Tag
        {...linkProps}
        className={`relative flex items-center justify-center p-10 md:p-14 group overflow-hidden bg-black block ${hasLink ? 'cursor-pointer' : 'cursor-default'}`}
        data-testid={`sponsor-${index}`}
      >
        {/* Fire glow — ambient */}
        <div
          className="absolute inset-0 opacity-100 group-hover:opacity-100 transition-opacity duration-700 pointer-events-none"
          style={{ background: 'radial-gradient(ellipse 55% 45% at 50% 55%, rgba(191,155,48,0.18) 0%, rgba(191,120,30,0.08) 35%, transparent 65%)' }}
        />
        {/* Hover fire pulse */}
        <div
          className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-500 pointer-events-none"
          style={{ background: 'radial-gradient(ellipse 45% 40% at 50% 60%, rgba(255,180,40,0.15) 0%, rgba(191,155,48,0.08) 35%, transparent 65%)' }}
        />
        {/* Bottom glow line */}
        <div className="absolute bottom-0 left-[15%] right-[15%] h-[1px] bg-gradient-to-r from-transparent via-gold/20 to-transparent group-hover:via-gold/50 transition-all duration-700" />
        {/* Content */}
        <div className="relative z-10 transform group-hover:scale-110 group-hover:-translate-y-0.5 transition-transform duration-500 ease-out">
          {sponsor.url && sponsor.media_type === 'image' ? (
            <img src={sponsor.url} alt={sponsor.title} className="max-h-12 opacity-40 group-hover:opacity-90 transition-all duration-500 grayscale group-hover:grayscale-0" />
          ) : (
            <span
              className="font-heading text-base md:text-lg tracking-[0.25em] uppercase text-center select-none block text-zinc-500 group-hover:text-gold transition-colors duration-500"
              style={{ textShadow: '0 0 20px rgba(191,155,48,0.25), 0 0 40px rgba(191,120,30,0.1), 0 2px 8px rgba(0,0,0,0.5)' }}
            >
              {sponsor.title}
            </span>
          )}
        </div>
      </Tag>
    </motion.div>
  );
}

export default function SponsorsSection({ sponsors }) {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-80px" });

  return (
    <section className="pt-10 md:pt-14 pb-10 md:pb-14 px-6 md:px-12 lg:px-24" data-testid="sponsors-section">
      <div className="max-w-6xl mx-auto" ref={ref}>
        <motion.div
          initial={{ opacity: 0 }}
          animate={isInView ? { opacity: 1 } : {}}
          transition={{ duration: 0.6 }}
          className="text-center mb-14"
        >
          <span className="text-gold text-[10px] tracking-[0.4em] uppercase font-body font-medium">
            Partners & Sponsors
          </span>
          <div className="w-16 h-[1px] bg-gold/40 mx-auto mt-4 mb-5" />
          <h2 className="font-heading text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-bold tracking-tight uppercase leading-[0.95]">
            Powering The<br />
            <span className="text-gold">Experience</span>
          </h2>
        </motion.div>

        <div className="grid grid-cols-2 md:grid-cols-4 gap-[1px] bg-white/[0.03]">
          {sponsors.map((sponsor, i) => (
            <SponsorBlock key={sponsor.id} sponsor={sponsor} index={i} isInView={isInView} />
          ))}
        </div>
      </div>
    </section>
  );
}
